import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'buyer_token';

const BuyerAuthContext = createContext(null);

export function BuyerAuthProvider({ children }) {
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);
  // Cache NDA status per listing so we don't re-fetch on every render
  const [ndaCache, setNdaCache] = useState({});

  // Restore session from stored token on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/buyer/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setBuyer(res.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  const _authHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const _persistSession = (token, buyerData) => {
    localStorage.setItem(TOKEN_KEY, token);
    setBuyer(buyerData);
    setNdaCache({});
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/buyer/login`, { email, password });
    _persistSession(res.data.access_token, res.data.buyer);
    return res.data.buyer;
  };

  const register = async (name, email, phone, password) => {
    const res = await axios.post(`${API}/buyer/register`, { name, email, phone, password });
    _persistSession(res.data.access_token, res.data.buyer);
    return res.data.buyer;
  };

  // Used by the email-verification link landing page: verify-email already
  // returns a fresh session (like login/register do), so just store it.
  const loginWithToken = (token, buyerData) => {
    _persistSession(token, buyerData);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setBuyer(null);
    setNdaCache({});
  };

  const checkEmail = async (email) => {
    const res = await axios.get(`${API}/buyer/check-email`, { params: { email } });
    return res.data.exists;
  };

  // Returns { signed: bool, signed_at? }
  const checkNDA = useCallback(async (listingId) => {
    if (ndaCache[listingId] !== undefined) return ndaCache[listingId];
    if (!buyer) return { signed: false };
    try {
      const res = await axios.get(`${API}/buyer/nda/check/${listingId}`, {
        headers: _authHeaders(),
      });
      setNdaCache(prev => ({ ...prev, [listingId]: res.data }));
      return res.data;
    } catch {
      return { signed: false };
    }
  }, [buyer, ndaCache]);

  const getNDAText = async (listingId) => {
    const res = await axios.get(`${API}/buyer/nda/text/${listingId}`, {
      headers: _authHeaders(),
    });
    return res.data; // { text, listing_name }
  };

  const getSignedNDAText = async (listingId) => {
    const res = await axios.get(`${API}/buyer/nda/signed-text/${listingId}`, {
      headers: _authHeaders(),
    });
    return res.data; // { text, listing_name }
  };

  const signNDA = async (listingId, listingName) => {
    const res = await axios.post(
      `${API}/buyer/nda/sign`,
      { listing_id: listingId, listing_name: listingName },
      { headers: _authHeaders() }
    );
    // Mark as signed in cache immediately
    setNdaCache(prev => ({
      ...prev,
      [listingId]: { signed: true, signed_at: res.data.signed_at },
    }));
    return res.data;
  };

  const getMyNDAs = async () => {
    const res = await axios.get(`${API}/buyer/nda/my-ndas`, { headers: _authHeaders() });
    return res.data;
  };

  const downloadNDAPdf = async (listingId, listingName) => {
    const res = await axios.get(`${API}/buyer/nda/nda-pdf/${listingId}`, {
      headers: _authHeaders(),
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    const safe = (listingName || 'Listing').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
    link.download = `TeamShailesh_NDA_${safe}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadBrochure = async (listingId, listingName) => {
    const res = await axios.get(`${API}/buyer/nda/brochure/${listingId}`, {
      headers: _authHeaders(),
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    const safe = (listingName || 'Listing').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_');
    link.download = `TeamShailesh_${safe}_Brochure.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Expose the current buyer token so listing pages can pass it in the
  // Authorization header to unlock NDA-protected fields server-side.
  const getToken = () => localStorage.getItem(TOKEN_KEY);

  return (
    <BuyerAuthContext.Provider value={{
      buyer,
      isAuthenticated: !!buyer,
      loading,
      ndaCache,
      login,
      register,
      loginWithToken,
      logout,
      checkEmail,
      checkNDA,
      getNDAText,
      getSignedNDAText,
      signNDA,
      downloadNDAPdf,
      getMyNDAs,
      downloadBrochure,
      getToken,
    }}>
      {children}
    </BuyerAuthContext.Provider>
  );
}

export function useBuyer() {
  const ctx = useContext(BuyerAuthContext);
  if (!ctx) throw new Error('useBuyer must be used inside BuyerAuthProvider');
  return ctx;
}
